import { GetConfigureFormOperations, Operation } from '@occc-app/offerHelpers/types';
import {
  getFulfillmentChannelConstraintOperations,
  getFulfillmentWindowOperations,
  getMinimumPurchaseConstraintOperations,
  getPaymentMethodConstraintOperations,
  getPreAuthConstraintOperations as getBasePreAuthConstraintOperations,
  makeRequiresActivationConstraintOperations,
} from '@occc/feat-constraints';
import { getLimitsInConstraintOperations, LimitType } from '@occc/feat-limits';
import { OfferFragment } from '@occc/graphql';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';

import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferFulfillmentPriorityUpdateOperations } from '@occc-app/offerHelpers/operations/getOfferFulfillmentPriorityUpdateOperations';
import { getScanDataConfigurationOperation } from '@occc-app/offerHelpers/operations/getScanDataConfigurationOperation';
import { getEarningMechanismOperations } from './earningMechanismOperations';
import { offerSchemaByType, requiresActivationApplicablePromotionTypes } from '../../../constants';
import { PromotionConfigureFormValues, PromotionType } from '../../../types';
import {
  getIsSupportPreAuthConstraint,
  getIsTransactionFrequencyLimitSupported,
  getIsTransactionLimitSupported,
} from '../../../utils';

const getDeleteOperationsForNotSupportedConstraints = (
  offer: OfferFragment | undefined,
  promotionType?: PromotionType | null,
): Operation[] => {
  if (!offer) {
    return [];
  }

  const operations: Operation[] = [];
  const supportedConstraints = promotionType ? offerSchemaByType[promotionType].constraints : [];

  offer.constraints.forEach((constraint) => {
    const isSupportedConstraint = supportedConstraints.some(
      (typename) => constraint.__typename === typename,
    );

    if (!isSupportedConstraint && 'id' in constraint) {
      operations.push(makeDeleteOperation('constraintDelete', constraint.id));
    }
  });

  return operations;
};

const getActivationLimitsDeleteOperations = (
  activation: PromotionConfigureFormValues['activation'],
): Operation[] => {
  const operations: Operation[] = [];

  if (activation.global.id) {
    operations.push(makeDeleteOperation('limitDelete', activation.global.id));
  }

  if (activation.membership.id) {
    operations.push(makeDeleteOperation('limitDelete', activation.membership.id));
  }

  return operations;
};

const getPreAuthConstraintOperations = (
  offer: OfferFragment | undefined,
  promotionType: PromotionType | null | undefined,
) => {
  const isSupportPreAuthConstraint = getIsSupportPreAuthConstraint(promotionType);
  if (!isSupportPreAuthConstraint) {
    return [];
  }

  return getBasePreAuthConstraintOperations(offer);
};

export const getConfigureFormOperations: GetConfigureFormOperations<
  PromotionConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getEarningMechanismOperations(offer, values, dirtyFields);

  const isActivationApplicable =
    !!values.offerType && requiresActivationApplicablePromotionTypes.includes(values.offerType);

  const activationConstraintAndLimitsOperations = isActivationApplicable
    ? [
        ...makeRequiresActivationConstraintOperations(
          offerId,
          values.activation,
          dirtyFields,
          offer,
        ),
        ...getLimitsInConstraintOperations(
          offerId,
          values.activation,
          dirtyFields,
          LimitType.Activation,
        ),
      ]
    : getActivationLimitsDeleteOperations(values.activation);

  const deleteOperationsForNotSupportedConstraints = getDeleteOperationsForNotSupportedConstraints(
    offer,
    values.offerType,
  );

  const offerUpdateOperation = makeOfferUpdateOperation(offerId, {
    stackable: values.offerType === PromotionType.FuelRollback ? values.stackable : false,
  });

  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );

  const constraintOperations = getMinimumPurchaseConstraintOperations(
    offerId,
    values,
    dirtyFields,
    offer,
  );

  const isTransactionLimitSupported = getIsTransactionLimitSupported(values.offerType);

  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
    {
      isTransactionLimitSupported,
      isTransactionFrequencyLimitSupported: getIsTransactionFrequencyLimitSupported(
        values.offerType,
      ),
      ...(offer ? { offer } : {}),
    },
  );

  const paymentMethodConstraintOperations = getPaymentMethodConstraintOperations(
    offerId,
    offer,
    values,
    dirtyFields,
  );

  const fulfillmentChannelConstraintOperations = getFulfillmentChannelConstraintOperations(
    offerId,
    offer,
    values,
  );

  const preAuthConstraintOperations = getPreAuthConstraintOperations(offer, values.offerType);

  const additionalOperations: Operation[] = [];
  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  const scanDataConfigurationOperation = getScanDataConfigurationOperation({
    offerId,
    values,
    dirtyFields,
  });
  if (scanDataConfigurationOperation) {
    additionalOperations.push(scanDataConfigurationOperation);
  }

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  additionalOperations.push(
    ...getOfferFulfillmentPriorityUpdateOperations(offerId, values, dirtyFields),
  );

  // ORDER is important
  // Can not mix awardCount limits and constraint

  return values.activation.isEnabled
    ? [
        ...earningMechanismOperations,
        ...deleteOperationsForNotSupportedConstraints,
        ...fulfillmentWindowOperations,
        ...constraintOperations,
        ...awardCountLimitsOperations,
        ...activationConstraintAndLimitsOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...preAuthConstraintOperations,
        offerUpdateOperation,
        ...additionalOperations,
      ]
    : [
        ...earningMechanismOperations,
        ...deleteOperationsForNotSupportedConstraints,
        ...fulfillmentWindowOperations,
        ...constraintOperations,
        ...activationConstraintAndLimitsOperations,
        ...awardCountLimitsOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...preAuthConstraintOperations,
        offerUpdateOperation,
        ...additionalOperations,
      ];
};
